/**
 * Зеркальная типизация ответов backend API.
 * Источник правды — laravel/routes/api.php + docs/api.md.
 */

export type Lang = 'ru' | 'en' | 'es' | 'de' | 'fr';
export type ShelfVisibility = 'all' | 'friends' | 'hidden';
export type CardType = 'insight' | 'quote' | 'ad';
export type ActionKind = 'like' | 'dislike' | 'bookmark' | 'share';
export type SubscriptionPlan = 'week' | 'month' | 'year';
export type FriendStatus = 'accepted';
export type TxType = 'subscription' | 'referral_payout' | 'withdrawal' | 'deposit';
export type TxStatus = 'pending' | 'completed' | 'failed';

export interface User {
  id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  lang: Lang;
  topics: string[];
  shelf_visibility: ShelfVisibility;
  ton_wallet: string | null;
  balance_ton: number;
  ref_code: string;
  ref_link: string;
  subscription_active: boolean;
  subscription_plan: SubscriptionPlan | null;
  subscription_expires_at: string | null;
  streak_days: number;
  cards_read_total: number;
  swipe_hint_seen?: boolean;
  analytics_opt_out?: boolean;
}

export interface Topic {
  id: string;
  icon: string;        // ti-* класс/имя
  name: string;
  books_count: number;
  cards_count: number;
}

export interface CardBook {
  id: string;
  title: string | null;
  author: string | null;
  year: number | null;
  purchase_url: string | null;
}

export interface CardStats {
  likes: number;
  dislikes: number;
  bookmarks: number;
  shares: number;
  comments: number;
}

export interface CardUserActions {
  liked: boolean;
  disliked: boolean;
  bookmarked: boolean;
}

export interface ContentCard {
  id: string;
  type: 'insight' | 'quote';
  topic_id: string;
  text: string | null;
  book: CardBook;
  stats: CardStats;
  user_actions: CardUserActions;
}

export interface AdCard {
  id: string;
  type: 'ad';
  campaign_id: number;
  brand: { name: string; logo_url: string | null };
  headline: string;
  description: string;
  cta: { label: string; url: string };
  remove_ads_offer: { price_ton: number };
}

export type FeedItem = ContentCard | AdCard;

export interface DailyProgress {
  cards_seen: number;
  limit: number;
}

export interface FeedMeta {
  daily_progress: DailyProgress;
  daily_limit_reached: boolean;
  next_card_in: number | null; // секунды до сброса лимита (на момент ответа сервера)
  limit_resets_at?: string | null; // абсолютный ISO-таймстамп окончания 12h-блока
}

export interface FeedResponse {
  data: FeedItem[];
  meta: FeedMeta;
}

export interface BookCardsResponse {
  data: {
    book: {
      id: string;
      title: string | null;
      author: string | null;
      year: number | null;
      purchase_url: string | null;
    };
    cards: ContentCard[];
  };
}

export interface ShelfItem {
  id: string;            // card_id
  book_id: string;
  book_title: string | null;
  book_author: string | null;
  topic_id: string;
  type: 'insight' | 'quote';
  text: string | null;
  saved_at: string;
  hidden_from_public: boolean;
  likes: number;
  dislikes?: number;
  bookmarks?: number;
  shares?: number;
  comments: number;
  user_actions: { liked: boolean; disliked?: boolean; bookmarked?: boolean };
}

export interface FriendShelfItem {
  id: string;
  text: string | null;
  book_title: string | null;
  book_id: string;
  stats: CardStats;
  user_actions: CardUserActions;
  saved_by: {
    id: number;
    first_name: string | null;
    username: string | null;
    photo_url: string | null;
  };
}

export interface CommentAuthor {
  id: number;
  first_name: string | null;
  username: string | null;
  photo_url: string | null;
}

export interface Comment {
  id: number;
  text: string;
  likes_count: number;
  liked_by_me?: boolean;
  created_at: string;
  author: CommentAuthor;
  replies: Array<Omit<Comment, 'replies' | 'replies_count'>>;
  replies_count: number;
}

export interface FriendItem {
  // null when this entry is a pure referral (ref_by_user_id=me but no friendship row).
  friendship_id: number | null;
  status: FriendStatus | null;
  created_at: string;
  invited_by_me: boolean;
  is_my_referral: boolean;
  user: {
    id: number;
    first_name: string | null;
    username: string | null;
    photo_url: string | null;
    shelf_visibility: ShelfVisibility;
    bookmarks_count?: number;
  };
}

export interface UserSearchItem {
  id: number;
  first_name: string | null;
  username: string | null;
  photo_url: string | null;
  is_friend: boolean;
  has_pending: boolean;
}

export interface HistoryItem {
  id: string;
  book_id: string;
  text: string | null;
  book_title: string | null;
  book_author: string | null;
  viewed_at: string;
}

export interface BookHistoryItem {
  book_id: string;
  title: string | null;
  author: string | null;
  seen: number;
  total: number;
  percent: number;
  last_seen: string;
}

export interface StreakDay {
  date: string; // YYYY-MM-DD
  active: boolean;
  count: number;
}

export interface FriendsListMeta {
  page: number;
  per_page: number;
  total: number;
  invited_by_me: number;
}

export interface ConnectFriendResponse {
  friendship_id: number;
  status: FriendStatus;
  inviter: {
    id: number;
    first_name: string | null;
    username: string | null;
    photo_url: string | null;
  };
}

export interface SubscriptionPaymentResponse {
  transaction_id: number;
  ton_payment: {
    address: string;
    amount_nano_ton: string;
    comment: string;
    deeplink: string;
  };
  expires_at: string;
}

export interface ReferralsResponse {
  ref_code: string;
  ref_link: string;
  invited_count: number;
  total_earned_ton: number;
  rate_percent: number;
  invitees: Array<{
    id: number;
    first_name: string | null;
    joined_at: string;
    earned_from_them_ton: number;
  }>;
}

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export interface ApiEnvelope<T> {
  data?: T;
  meta?: Record<string, unknown>;
  error?: ApiError;
}

export type PaymentMethod = 'ton' | 'card';
export type CardGateway = 'russian' | 'international';

export interface SubscriptionPlanInfo {
  plan: SubscriptionPlan;
  price_ton: number;
  price_card_display: string;
  days: number;
}

export interface TransactionItem {
  id: number;
  type: TxType;
  amount_ton: string;
  status: TxStatus;
  ton_tx_hash: string | null;
  related_user_id: number | null;
  meta: Record<string, unknown> | null;
  created_at: string;
}

/* ---- runtime guards ----------------------------------------------------- */

export function isAdCard(c: FeedItem): c is AdCard {
  return c.type === 'ad';
}

export type NotificationKind =
  | 'comment_like'
  | 'comment_reply'
  | 'streak_milestone'
  | 'subscription_activated'
  | 'subscription_ending'
  | 'referral_joined'
  | 'referral_bonus'
  | 'daily_reminder'
  | 'book_finished'
  | 'friend_shelved_book';

export type NotificationPrefs = Record<NotificationKind, boolean>;
